import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { Button, Input, Alert } from '../components/ui'

type SignUpForm = {
  email: string
  password: string
  fullName: string
}

const DEV_CREDENTIALS: Record<string, { email: string; password: string; fullName: string }> = {
  'chipdev': { email: 'chip@test.com', password: 'testing123', fullName: 'Chip Test' },
  'lucadev': { email: 'luca@test.com', password: 'testing123', fullName: 'Luca Test' },
}

function getDevCredentials() {
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const devHost = import.meta.env.VITE_DEV_HOST || ''
    return DEV_CREDENTIALS[devHost] || null
  }
  return null
}

const SignUpPage: React.FC = () => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const { register: registerUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const redirectTo = searchParams.get('redirect')
  const prefillEmail = searchParams.get('email')
  const devCreds = getDevCredentials()

  // Schema is built inside the component so validation messages localize.
  const signUpSchema = z.object({
    email: z.string().email(intl.formatMessage({ id: 'auth.validation.emailInvalid' })),
    password: z.string().min(6, intl.formatMessage({ id: 'auth.validation.passwordMin' })),
    fullName: z.string().min(1, intl.formatMessage({ id: 'auth.validation.fullNameRequired' })),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
    defaultValues: devCreds
      ? { email: devCreds.email, password: devCreds.password, fullName: devCreds.fullName }
      : prefillEmail
        ? { email: prefillEmail }
        : undefined,
  })

  const onSubmit = async (data: SignUpForm) => {
    try {
      setIsLoading(true)
      setError('')
      await registerUser(data.email, data.password, data.fullName)
      navigate(redirectTo || '/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.detail || intl.formatMessage({ id: 'auth.signup.failed' }))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignup = () => {
    const returnTo = encodeURIComponent(window.location.origin)
    window.location.href = `${API_ENDPOINTS.auth.google}?return_to=${returnTo}`
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            <FormattedMessage id="auth.signup.title" />
          </h2>
          <p className="mt-2 text-center text-sm text-foreground-muted">
            <FormattedMessage id="auth.signup.orText" />{' '}
            <Link to="/auth/signin" className="font-medium text-primary hover:text-primary">
              <FormattedMessage id="auth.signup.signInLink" />
            </Link>
          </p>
        </div>

        <div>
          <Button variant="outline" fullWidth onClick={handleGoogleSignup} className="gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <FormattedMessage id="auth.google" />
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-background text-foreground-muted"><FormattedMessage id="auth.signup.orSignUpWithEmail" /></span>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <Input
            {...register('fullName')}
            label={intl.formatMessage({ id: 'auth.fullName' })}
            id="fullName"
            type="text"
            autoComplete="name"
            placeholder={intl.formatMessage({ id: 'auth.fullNamePlaceholder' })}
            error={errors.fullName?.message}
          />

          <Input
            {...register('email')}
            label={intl.formatMessage({ id: 'auth.emailAddress' })}
            id="email"
            type="email"
            autoComplete="email"
            placeholder={intl.formatMessage({ id: 'auth.emailPlaceholder' })}
            error={errors.email?.message}
          />

          <Input
            {...register('password')}
            label={intl.formatMessage({ id: 'auth.password' })}
            id="password"
            type="password"
            autoComplete="new-password"
            placeholder={intl.formatMessage({ id: 'auth.passwordPlaceholder' })}
            error={errors.password?.message}
          />

          <Button type="submit" fullWidth loading={isLoading}>
            {isLoading
              ? intl.formatMessage({ id: 'auth.signup.creating' })
              : intl.formatMessage({ id: 'auth.signup.create' })}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default SignUpPage
